<template>
  <Dialog :open="showModal" @update:open="onDialogUpdate" prevented>
    <DialogTrigger as-child>
      <Button>
        {{ $t('Start a new test') }}
      </Button>
    </DialogTrigger>
    <DialogScrollContent class="tw:bg-zinc-50">
      <DialogHeader>
        <DialogTitle>
          {{ $t('Product test information') }}
        </DialogTitle>
      </DialogHeader>
      <div>
        <div class="mb-3">
          <Alert variant="danger" v-if="!errors.expected_at" v-for="error in errors">
            <template v-if="Array.isArray(error)">
              {{ error.join(', ') }}
            </template>
            <template v-else>
              {{ error }}
            </template>
          </Alert>
          <!-- Registration -->
          <div class="card tw:border-b-0 tw:rounded-b-none" v-if="lendingParameters.registration">
            <div class="card-body tw:p-3">
              <RegistrationInfo :registration="lendingParameters.registration" />
            </div>
          </div>

          <!-- Item -->
          <template v-if="lendingParameters.equipment">
            <div class="card mb-3 tw:rounded-t-none">
              <div class="card-body tw:p-3">
                <div class="tw:flex tw:items-center tw:gap-3 tw:justify-between">
                  <EquipmentInfo :item="lendingParameters.equipment" class="tw:flex-1" />

                  <div>
                    <a href @click.prevent="lendingParameters.equipment = null" class="text-danger">
                      <MinusCircle class="tw:size-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <button @click="showEquipmentDialog = true"
              class="tw:block tw:w-full tw:bg-white tw:hover:bg-zinc-50/50 tw:flex tw:items-center tw:gap-2 tw:border tw:p-3 tw:rounded-b-md tw:text-blue-600"
              type="button">
              <CirclePlus class="tw:size-5" />
              {{ $t('Add an item') }}
            </button>
          </template>
        </div>
        <div class="tw:mt-4 tw:space-y-4">
          <template v-if="lendingParameters.registration  && lendingParameters.equipment">
            <div class="card tw:overflow-hidden">
              <div class="card-body tw:p-0">

                <!-- Expected return date -->
                <div>
                  <template v-if="showExpectedDate">
                    <div class="tw:p-3">
                      <div class="tw:flex tw:items-center tw:gap-3 tw:justify-between">
                        <div class="tw:flex-1">
                          <label for="frmLendingDueDate">{{ $t('Expected return') }} {{ $t('(optional)') }}</label>
                          <Datetime id="frmLendingDueDate" v-model="lendingParameters.expected_at" :min="minDateTime"
                            :timezone="timezone" with-timezone :class-name="errors.expected_at ? 'is-invalid' : ''">
                          </Datetime>
                          <div v-if="errors.expected_at" class="text-danger small">
                            {{ errors.expected_at.join(', ') }}
                          </div>
                        </div>
                        <div>
                          <button @click="showExpectedDate = false; lendingParameters.expected_at = null"
                            class="text-danger tw:border-0 tw:bg-transparent tw:p-0">
                            <MinusCircle class="tw:size-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <button
                      class="tw:block tw:w-full tw:bg-white-500 tw:hover:bg-zinc-50/50 tw:flex tw:items-center tw:gap-2 tw:p-3 tw:text-blue-600"
                      @click="showExpectedDate = true">
                      <CalendarArrowUp class="tw:size-5" />
                      {{ $t('Set expected return date') }}
                    </button>
                  </template>
                </div>

                <!-- Comment -->
                <div class="tw:border-t">
                  <template v-if="showComment">
                    <div class="tw:p-3">
                      <div class="tw:flex tw:items-center tw:gap-3 tw:justify-between">
                        <div class="tw:flex-1">
                          <label for="frmLendingComment">{{ $t('Comment') }} {{ $t('(optional)') }}</label>
                          <textarea id="frmLendingComment" class="form-control" cols="30" rows="2"
                            v-model="lendingParameters.comment"></textarea>
                        </div>
                        <div>
                          <button @click="showComment = false; lendingParameters.comment = ''"
                            class="text-danger tw:border-0 tw:bg-transparent tw:p-0">
                            <MinusCircle class="tw:size-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <button
                      class="tw:w-full tw:bg-white-500 tw:hover:bg-zinc-50/50 tw:flex tw:items-center tw:gap-2 tw:p-3 tw:text-blue-600"
                      @click="showComment = true">
                      <MessageSquare class="tw:size-5" />
                      {{ $t('Add comment') }}
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </template>


          <Button
            class="tw:mt-4 mt-2 btn-block tw:flex tw:items-center tw:justify-center tw:gap-2"
            :disabled="!enableSubmitButton" @click="startLending"
            variant="success"
            size="lg"
            :loading="startLendingLoading"
          >
            <i class="fa fa-play-circle tw:!text-xl"></i>
            {{ $t('Start test') }}
          </Button>
        </div>
      </div>
    </DialogScrollContent>
  </Dialog>

  <!-- Equipment Selection Dialog -->
  <Dialog :open="showEquipmentDialog" @update:open="showEquipmentDialog = $event">
    <DialogScrollContent size="xl">
      <DialogHeader>
        <DialogTitle>
          {{ $t('Select an item') }}
        </DialogTitle>
      </DialogHeader>
      <div>
        <EquipmentIndex
          :base-filters="equipmentBaseFiltersComputed"
          :available-filters="availableEquipmentFilters"
          :product-metafields="productMetafields"
          :equipment-metafields="equipmentMetafields"
          :display="display">
          <template v-slot:default="{ equipments }">
            <EquipmentsClickable v-if="equipments" :equipments="equipments" @equipment-selected="selectEquipment">
            </EquipmentsClickable>
          </template>
        </EquipmentIndex>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup>
  import { ref, defineProps, computed, onMounted } from "vue";
  import { emit as emitEvent } from '@/eventBus.js';
  import dayjs from "dayjs";
  import Datetime from '@/components/ui/Datetime.vue'
  import { Dialog, DialogScrollContent, DialogTrigger, DialogHeader, DialogTitle } from "@/components/ui/dialog";
  import Alert from "@/components/ui/Alert.vue";
  import EquipmentIndex from "@/components/equipment/EquipmentIndex.vue";
  import EquipmentsClickable from "@/components/equipment/EquipmentsClickable.vue";
  import { CirclePlus, MinusCircle, CalendarArrowUp, MessageSquare } from '@lucide/vue';
  import { toast } from 'vue-sonner'
  import { useI18n } from "vue-i18n";
  import RegistrationInfo from "@/components/registration/RegistrationInfo.vue";
  import EquipmentInfo from "@/components/equipment/EquipmentInfo.vue";
  import { Button } from "@/shadcn/components/ui/button";
  const { t } = useI18n()

  const props = defineProps({
    booking: {
      default : null,
    },
    equipment: {
      default : null,
    },
    registration : {
      default: null,
    },
    lendingUrl: {
      default: null,
    },
    timezone: {
      default: 'UTC',
    },
    baseFilters: {
      type: Object,
      default: () => ({})
    },
    availableEquipmentFilters: {
      type: Array,
      default: () => []
    },
    productMetafields: {
      type: Array,
      default: () => []
    },
    equipmentMetafields: {
      type: Array,
      default: () => []
    },
    display: {
      type: Array,
      default: () => ['brand']
    },
  });

  const enableSubmitButton = computed(() => {
    return lendingParameters.value.registration && lendingParameters.value.equipment;
  })

  const minDateTime = computed(() => {
    // UTC now at ISO 8601 String format
    return dayjs().utc().format();
  })

  const equipmentBaseFiltersComputed = computed(() => {
    return props.baseFilters;
  })

  const errors = ref([])
  const lendingParameters = ref({
    registration: props.registration,
    equipment: props.equipment,
    booking: props.booking,
    expected_at: props.booking?.end_at
  })
  const showComment = ref(false)
  const showExpectedDate = ref(!!props.booking?.end_at)
  const showEquipmentDialog = ref(false)
  const startLendingLoading = ref(false)

  const showModal = ref(false);
  const onDialogUpdate = (isOpen) => {
    showModal.value = isOpen;
    if (isOpen) {
      errors.value = [];
      lendingParameters.value = {
        registration: props.registration,
        equipment: props.equipment,
        booking: props.booking,
        expected_at: props.booking?.end_at
      }
    }
  }

  const startLending = () => {
    errors.value = [];
    startLendingLoading.value = true;

    if(! props.lendingUrl) {
      startLendingLoading.value = false;
      return;
    }
    axios.post(props.lendingUrl, {
      booking : lendingParameters.value.booking ? lendingParameters.value.booking.id : null,
      equipment : lendingParameters.value.equipment  ? lendingParameters.value.equipment.id : null,
      registration : lendingParameters.value.registration ? lendingParameters.value.registration.id : null,
      comment : lendingParameters.value.comment,
      expected_at : lendingParameters.value.expected_at,
    })
      .then(() => {
        emitEvent('start');
        showModal.value = false;

        setTimeout(() => {
          toast.success(t('The test started'))
        }, 200)
      })
      .catch(function (error) {
        errors.value = error.response.data.errors
        if (error.response.data.message) {
          errors.value = error.response.data.message
        }
      })
      .finally(() => {
        startLendingLoading.value = false;
      });
  }

  const selectEquipment = function(equipment) {
    errors.value = [];
    lendingParameters.value.equipment = equipment;
    showEquipmentDialog.value = false;
  }
</script>
